import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="34676" className="hidden md:flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.id === "86" || item.id === "88" ? "/ContactUsPage" : item.href || "#"} className="group relative text-sm font-medium uppercase tracking-[0.12em] text-foreground/90 transition-colors hover:text-primary font-sans">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <div className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.id === "86" || item.id === "88" ? "/ContactUsPage" : item.href || "#"} className="text-lg font-medium uppercase tracking-widest text-foreground/90 hover:text-primary transition-colors font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-[12px] border-border/20 shadow-xl h-16" : "bg-background border-border/10 h-20"}`}>
      <div className="container mx-auto px-6 h-full flex items-center justify-between">
        
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619832/img/momentum-consultants-logo.png" alt="Momentum Consultants Pty Ltd" className="h-7 w-auto object-contain transition-transform duration-300 hover:scale-105" />
        </Link>

        
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="46">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="hidden md:flex items-center">
          <Button variant="default" asChild className="shadow-md transition-all duration-300 rounded-lg px-6 font-medium tracking-tight">
            <Link to="#">Request Consultation</Link>
          </Button>
        </div>

        
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      
      <div className={`fixed inset-0 z-40 bg-background/98 backdrop-blur-2xl transition-transform duration-500 md:hidden pt-24 ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
        <MenuProvider menu_id="46">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
        
        <div className="p-6 mt-4 border-t border-border/10">
          <Button variant="default" asChild className="w-full shadow-lg transition-all duration-300 rounded-lg py-6 text-lg font-medium">
            <Link to="#">Request Consultation</Link>
          </Button>
        </div>
      </div>
    </header>;
}